<template>
  <div class="plans-page">
    <AppHeader />
    <div class="plans-container">
      <div class="plans-header">
        <h1>{{ $t('legacy.Выберите тариф') }}</h1>
        <p>{{ $t('legacy.Получите полный доступ ко всем функциям сервиса') }}</p>
      </div>

      <div v-if="loading" class="loading-state">
        <p>{{ $t('legacy.Загрузка тарифов...') }}</p>
      </div>

      <div v-else class="plans-grid">
        <div 
          v-for="plan in plans" 
          :key="plan.id" 
          class="plan-card"
          :class="{ 'popular': plan.popular }"
        >
          <div v-if="plan.popular" class="popular-badge">
             {{ $t('legacy.Популярный') }}
          </div>
          <div class="plan-name">{{ getPlanName(plan) }}</div>
          <div v-if="getPlanPrice(plan) !== null" class="plan-price">
            <span class="price-value">{{ formatPlanPrice(plan) }}</span>
            <span class="price-currency">{{ currentLocale === 'en' ? '$' : '₽' }}</span>
             <span class="price-period">{{ $t('legacy./месяц') }}</span>
          </div>
          <div v-else class="plan-description">{{ $t(currentLocale === 'en' ? 'pricing.unavailable' : 'pricing.rubUnavailable') }}</div>
          <div class="plan-description">{{ $t(plan.description) }}</div>
          
          <ul class="plan-features">
            <li v-for="(feature, idx) in plan.features" :key="idx">
              <span class="feature-icon">✓</span>
              {{ formatFeature(feature, plan.aiCredits) }}
            </li>
          </ul>
          
          <div class="plan-credits">
            <span class="credits-icon">⚡</span>
             {{ $t('pricing.aiCredits', { count: plan.aiCredits }) }}
             <span v-if="plan.canBuyCredits" class="credits-note">({{ $t('legacy.можно докупать') }})</span>
          </div>
          
          <button 
            class="select-plan-btn" 
            :class="{ 'primary': plan.popular }"
            :disabled="getPlanPrice(plan) === null"
            @click="selectPlan(plan)"
          >
             {{ $t('legacy.Выбрать') }}
          </button>
        </div>
      </div>

      <div class="back-link">
         <a href="#" @click.prevent="goBack">{{ $t('legacy.← Вернуться назад') }}</a>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue';
import { getSubscriptionPlans } from '@/api/subscription';
import { locale } from '@/i18n';

export default {
  name: 'SubscriptionPlansPage',
  components: {
    AppHeader,
  },
  data() {
    return {
      loading: true,
      plans: [],
    };
  },
  computed: {
    currentLocale() {
      return locale.value;
    },
  },
  async mounted() {
    await this.loadPlans();
  },
  methods: {
    getPlanName(plan) {
      if (this.currentLocale === 'en') {
        const names = { base: 'Base', premium: 'Premium' };
        return names[plan.id] || this.$t(plan.name);
      }
      return this.$t(plan.name);
    },
    getPlanPrice(plan) {
      const value = this.currentLocale === 'en' ? plan.priceUsd : plan.price;
      if (value === null || value === undefined || String(value).trim() === '') return null;
      const amount = Number(value);
      return Number.isFinite(amount) && amount > 0 ? amount : null;
    },
    formatPlanPrice(plan) {
      return this.$formatNumber(this.getPlanPrice(plan), {
        minimumFractionDigits: this.currentLocale === 'en' ? 2 : 0,
        maximumFractionDigits: 2,
      });
    },
    formatFeature(feature, credits) {
      if (/^\d+\s+AI-кредит/.test(feature)) {
        return this.$t('pricing.aiCredits', { count: credits });
      }
      return this.$t(feature);
    },
    async loadPlans() {
      this.loading = true;
      try {
        this.plans = await getSubscriptionPlans();
      } catch (error) {
        console.error('Error loading plans:', error);
      } finally {
        this.loading = false;
      }
    },
    selectPlan(plan) {
      const price = this.getPlanPrice(plan);
      if (price === null) return;
      this.$router.push({
        path: '/subscription/payment',
        query: { plan: plan.id, price }
      });
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/personal-subscription');
      }
    }
  }
};
</script>

<style scoped>
.plans-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa, #e8ecef);
}

.plans-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
}

.plans-header {
  text-align: center;
  margin-bottom: 40px;
}

.plans-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin: 0 0 12px 0;
}

.plans-header p {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.loading-state {
  text-align: center;
  padding: 60px;
  color: #666;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 40px;
}

.plan-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
}

.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.plan-card.popular {
  border: 2px solid #496782;
  transform: scale(1.02);
}

.plan-card.popular:hover {
  transform: scale(1.02) translateY(-4px);
}

.popular-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #496782, #5a7a99);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 20px;
  white-space: nowrap;
}

.plan-name {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
}

.plan-price {
  margin-bottom: 8px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
}

.price-value {
  font-size: 48px;
  font-weight: 700;
  color: #496782;
}

.price-currency {
  font-size: 24px;
  color: #496782;
}

.price-period {
  font-size: 14px;
  color: #999;
}

.plan-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
  text-align: left;
  flex-grow: 1;
}

.plan-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 14px;
  color: #444;
  border-bottom: 1px solid #f0f0f0;
}

.plan-features li:last-child {
  border-bottom: none;
}

.feature-icon {
  color: #4CAF50;
  font-weight: bold;
  font-size: 16px;
}

.plan-credits {
  background: linear-gradient(135deg, #fff8e1, #fff3c4);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #ff9800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.credits-icon {
  font-size: 18px;
}

.credits-note {
  font-weight: 400;
  font-size: 12px;
  color: #888;
}

.select-plan-btn {
  width: 100%;
  padding: 14px 24px;
  background: #f0f0f0;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
}

.select-plan-btn:hover {
  background: #e0e0e0;
}

.select-plan-btn.primary {
  background: linear-gradient(135deg, #496782, #5a7a99);
  color: #fff;
}

.select-plan-btn.primary:hover {
  background: linear-gradient(135deg, #3d5a70, #4d6d8c);
}

.back-link {
  text-align: center;
}

.back-link a {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}

.back-link a:hover {
  color: #496782;
}

@media (max-width: 768px) {
  .plans-container {
    padding: 20px 16px;
  }

  .plans-header h1 {
    font-size: 24px;
  }

  .plans-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .plan-card.popular {
    transform: none;
  }

  .plan-card.popular:hover {
    transform: translateY(-4px);
  }
  
  .price-value {
    font-size: 36px;
  }
}
</style>
